import React, { useEffect, useState } from 'react';
import { Input, Label } from 'reactstrap';
import {
    QueryBuilder as StatusPendingIcon,
    AssignmentTurnedInOutlined as StatusApprovedIcon,
    HighlightOffOutlined as StatusRejectedIcon,
    DoNotDisturbAltOutlined as StatusInactiveIcon
} from '@mui/icons-material';

import { UserApprovalItem, UserApprovalStatusEnum } from 'shared/models/api/models';
import { getUserApprovals, approveUserApproval, rejectUserApproval, reactivateUserApproval } from 'shared/services/userApprovalsApi';
import { PageList } from "components/ui/PageList";
import { Toggle } from "components/ui/ToggleContent";
import { ConfirmationBox, IConfirmProps } from "components/ui/ConfirmationBox";


interface IPageSizeDropdownProps {
    pageSize: number,
    setPageSize: (pageSize: number) => void,
    sizes: number[]
}

const PageSizeDropdown = (props: IPageSizeDropdownProps) => {
    return (
        <span className="psl-list-container">
            <div className="psl-list">
                {props.sizes.map(s =>
                    <div key={s}
                        className={`psl-item${ s === props.pageSize ? " selected" : "" }`}
                        onClick={() => props.setPageSize(s)}>{s} items</div>
                )}
            </div>
        </span>
    );
};

const PageSizeWithPicker = (props: IPageSizeDropdownProps) => {
    return (
        <Toggle label={props.pageSize + " items"} collapseOnChangeOf={props.pageSize}>
            <PageSizeDropdown {...props} />
        </Toggle>
    );
};


// Status display metadata, keyed by state. Keeps the cell, filter labels and styling consistent.
const STATUS_META: { status: UserApprovalStatusEnum, label: string, className: string, Icon: any }[] = [
    { status: UserApprovalStatusEnum.Pending, label: "Pending", className: "ua-status-pending", Icon: StatusPendingIcon },
    { status: UserApprovalStatusEnum.Approved, label: "Approved", className: "ua-status-approved", Icon: StatusApprovedIcon },
    { status: UserApprovalStatusEnum.Rejected, label: "Rejected", className: "ua-status-rejected", Icon: StatusRejectedIcon },
    { status: UserApprovalStatusEnum.Inactive, label: "Inactive", className: "ua-status-inactive", Icon: StatusInactiveIcon }
];

const metaFor = (status: UserApprovalStatusEnum) =>
    STATUS_META.find(m => m.status === status) ?? STATUS_META[0];

// Approved accounts are intentionally not surfaced on this dashboard: once approved, a request is
// no longer an action item. Approved stays in STATUS_META only so a row can render briefly if needed.
const FILTER_STATUS_META = STATUS_META.filter(m => m.status !== UserApprovalStatusEnum.Approved);


const StatusCell = (props: { status: UserApprovalStatusEnum }) => {
    const meta = metaFor(props.status);
    const Icon = meta.Icon;
    return (
        <span className={`ua-status ${ meta.className }`} title={meta.label}>
            <Icon /> <span className="ua-status-text">{meta.label}</span>
        </span>
    );
};


interface ApprovalRowProps {
    item: UserApprovalItem,
    onApproveClick: (item: UserApprovalItem) => void,
    onRejectClick: (item: UserApprovalItem) => void,
    onReactivateClick: (item: UserApprovalItem) => void
}

const ApprovalRow = (props: ApprovalRowProps) => {
    const { item } = props;
    const dateFormat = new Intl.DateTimeFormat("en-AU", {});
    // New sign-ups (Pending) can be approved or rejected. A deactivated account that has requested
    // reactivation (Inactive) can be reactivated. Other states have no action.
    const isPending = item.status === UserApprovalStatusEnum.Pending;
    const isReactivationRequest = item.status === UserApprovalStatusEnum.Inactive;

    return (
        <div className="row approval-card-row ua-card">
            <div className="ua-col-status">
                <StatusCell status={item.status} />
            </div>
            <div className="ua-col-date d-none d-md-inline-block">
                {item.dateRequested ? dateFormat.format(new Date(item.dateRequested)) : "-"}
            </div>
            <div className="col ua-col-name">{item.name}</div>
            <div className="col ua-col-manager d-none d-lg-inline-block">{item.contractManagerName}</div>
            <div className="ua-col-action">
                {isPending ?
                    <div className="ua-action-buttons">
                        <button
                            type="button"
                            className="btn btn-primary ua-approve-btn"
                            onClick={() => props.onApproveClick(item)}>
                            Approve
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-danger ua-reject-btn"
                            onClick={() => props.onRejectClick(item)}>
                            Reject
                        </button>
                    </div>
                    : isReactivationRequest ?
                        <div className="ua-action-buttons">
                            <button
                                type="button"
                                className="btn btn-primary ua-reactivate-btn"
                                onClick={() => props.onReactivateClick(item)}>
                                Reactivate
                            </button>
                        </div>
                        :
                        <span className="ua-no-action">&mdash;</span>
                }
            </div>
        </div>
    );
};


interface UserApprovalsListProps {
    title: string
}

const UserApprovalsList = (props: UserApprovalsListProps) => {
    const [items, setItems] = useState([] as UserApprovalItem[]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(25);
    const [pageNumber, setPageNumber] = useState(1);
    // Default to showing only the currently logged-in manager's action items.
    const [onlyMine, setOnlyMine] = useState(true);
    // Account states to show (Approved is not shown on this dashboard). All selected by default.
    const [statusFilters, setStatusFilters] = useState(FILTER_STATUS_META.map(m => m.status));
    const [confirmProps, setConfirmProps] = useState(null as IConfirmProps | null);

    const loadData = async () => {
        setLoading(true);
        const data = await getUserApprovals({ onlyMine, statuses: statusFilters });
        setItems(data ?? []);
        setPageNumber(1);
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, [onlyMine, statusFilters]);

    const moveToPage = (page: number) => setPageNumber(page);

    const toggleStatus = (status: UserApprovalStatusEnum, checked: boolean) => {
        setStatusFilters(prev =>
            checked ? [...prev, status] : prev.filter(s => s !== status)
        );
    };

    const doApprove = async (item: UserApprovalItem) => {
        const result = await approveUserApproval(item.id);
        if (result) {
            // Approved accounts drop off the dashboard - they are no longer action items.
            setItems(prev => prev.filter(i => i.id !== item.id));
        }
    };

    const doReject = async (item: UserApprovalItem) => {
        const result = await rejectUserApproval(item.id);
        if (result) {
            setItems(prev => prev.map(i =>
                i.id === item.id ? { ...i, status: UserApprovalStatusEnum.Rejected } : i
            ));
        }
    };

    const doReactivate = async (item: UserApprovalItem) => {
        const result = await reactivateUserApproval(item.id);
        if (result) {
            // Reactivated accounts are active again - no longer an action item, so drop from the list.
            setItems(prev => prev.filter(i => i.id !== item.id));
        }
    };

    const onApproveClick = (item: UserApprovalItem) => {
        setConfirmProps({
            message: (
                <div className="ua-confirm-message">
                    <div className="ua-confirm-title">Approve user access</div>
                    <div>
                        Approve access for <strong>{item.name}</strong>
                        {item.company ? <> ({item.company})</> : null}?
                    </div>
                </div>
            ),
            options: ["Cancel", "Approve"],
            defaultOption: "Approve",
            setResult: (value: string) => {
                setConfirmProps(null);
                if (value === "Approve") {
                    doApprove(item);
                }
            }
        } as IConfirmProps);
    };

    const onRejectClick = (item: UserApprovalItem) => {
        setConfirmProps({
            message: (
                <div className="ua-confirm-message">
                    <div className="ua-confirm-title">Reject user access</div>
                    <div>
                        Reject the access request for <strong>{item.name}</strong>
                        {item.company ? <> ({item.company})</> : null}? They will be notified their
                        request was declined.
                    </div>
                </div>
            ),
            options: ["Cancel", "Reject"],
            defaultOption: "Reject",
            setResult: (value: string) => {
                setConfirmProps(null);
                if (value === "Reject") {
                    doReject(item);
                }
            }
        } as IConfirmProps);
    };

    const onReactivateClick = (item: UserApprovalItem) => {
        setConfirmProps({
            message: (
                <div className="ua-confirm-message">
                    <div className="ua-confirm-title">Reactivate user access</div>
                    <div>
                        Reactivate access for <strong>{item.name}</strong>
                        {item.company ? <> ({item.company})</> : null}? They will be able to sign in
                        again and will be notified.
                    </div>
                </div>
            ),
            options: ["Cancel", "Reactivate"],
            defaultOption: "Reactivate",
            setResult: (value: string) => {
                setConfirmProps(null);
                if (value === "Reactivate") {
                    doReactivate(item);
                }
            }
        } as IConfirmProps);
    };

    const start = (pageNumber - 1) * pageSize;
    const displayItems = items.slice(start, start + pageSize);

    return (
        <div className="ua-list">
            <div className="ua-list-top">
                <div className="ua-list-title">{props.title}</div>
                <div className="ua-page-size">
                    <PageSizeWithPicker
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        sizes={[10, 25, 50, 100]}
                    />
                </div>
            </div>

            <div className="ua-filters">
                <div className="ua-filter-group">
                    <span className="ua-filter-label">Show accounts:</span>
                    {FILTER_STATUS_META.map(meta => {
                        const id = `ua-status-${ meta.status }`;
                        return (
                            <div key={meta.status} className={`ua-filter-check ua-filter-chip ${ meta.className } form-check`}>
                                <Input
                                    type="checkbox"
                                    id={id}
                                    checked={statusFilters.includes(meta.status)}
                                    onChange={(e) => toggleStatus(meta.status, e.target.checked)}
                                />
                                <Label for={id} check>{meta.label}</Label>
                            </div>
                        );
                    })}
                </div>
                <div className="ua-filter-group">
                    <div className="ua-filter-check form-check">
                        <Input
                            type="checkbox"
                            id="ua-only-mine"
                            checked={onlyMine}
                            onChange={(e) => setOnlyMine(e.target.checked)}
                        />
                        <Label for="ua-only-mine" check>Show only my action items</Label>
                    </div>
                </div>
            </div>

            <div className="ua-list-body">
                {loading ?
                    <div className="p-4 text-center">Loading...</div>
                    :
                    <>
                        <div className="row approval-card-row ua-list-header">
                            <div className="ua-col-status">Status</div>
                            <div className="ua-col-date d-none d-md-inline-block">Date Requested</div>
                            <div className="col ua-col-name">Name</div>
                            <div className="col ua-col-manager d-none d-lg-inline-block">Contract Manager</div>
                            <div className="ua-col-action">Action</div>
                        </div>

                        <div className="ua-divider row"></div>

                        {displayItems.length > 0 ?
                            displayItems.map(item => (
                                <ApprovalRow
                                    key={item.id}
                                    item={item}
                                    onApproveClick={onApproveClick}
                                    onRejectClick={onRejectClick}
                                    onReactivateClick={onReactivateClick}
                                />
                            ))
                            :
                            <div className="p-4 text-center">There are no user accounts matching the selected filters.</div>
                        }
                    </>
                }
            </div>

            <div className="ua-list-footer">
                <div className="ua-result-count">{items.length} account{items.length === 1 ? "" : "s"}</div>
                <div className="page-list">
                    <PageList
                        totalResults={items.length}
                        pageSize={pageSize}
                        pageNumber={pageNumber}
                        moveToPage={moveToPage}
                    />
                </div>
            </div>

            {confirmProps && <ConfirmationBox {...confirmProps} />}
        </div>
    );
};

export default UserApprovalsList;
